import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ButtonGroup, Tab } from 'react-bootstrap'
import { useLatest, useLocalStorage, useMeasure, useWindowSize } from 'react-use'
import toNumber from 'lodash/toNumber'
import type { Chat } from '@ztechapps/common.contract'
import type { ConfirmDialogOptions } from '@ztechapps/common.hook'
import type { BreadcrumbItem } from '@ztechapps/webui.page'
import type {
  ConversationTemplateVariablesProviderProps,
  CreateOrderFromConversationButtonProps,
} from '@ztechapps/webui.contract'
import type { TemplateVariables } from '../Conversation/Context'
import { ChatCancelReasonEnum, ChatStatusEnum } from '@ztechapps/common.contract'
import {
  chatFragment,
  chatPermissions,
  useChatMessage,
  useMarkChatRead,
  useSendChatMessage,
} from '@ztechapps/chat.common'
import { buildGraphqlQuery, isBlankHtml, truncate } from '@ztechapps/common.utils'
import { useDocument, useError, useIsMobileScreenSize, useMutation, useRole, useToast } from '@ztechapps/common.hook'
import { BusyButton, GoBackButton, RefreshButton } from '@ztechapps/webui.button'
import { HistoryLayout } from '@ztechapps/webui.data-tools'
import { Breadcrumb, CollapsibleSidebar, FixedPage } from '@ztechapps/webui.page'
import { WebuiIocKey, useInjectComponent } from '@ztechapps/webui.contract'
import {
  EmptyContent,
  ErrorContent,
  HorizontalStack,
  Icon,
  NullComponent,
  ShowIf,
  TabTitle,
  TabWithSearchParams,
  TooltipPopOver,
  useDragResize,
} from '@ztechapps/webui.util'
import { useChannelContext } from '../Channel'
import { RichTextChatInput } from '../Channel/RichTextChatInput'
import { useConversationHost } from '../ConversationHost'
import {
  ComposerModeEnum,
  ConversationForEnum,
  ConversationProvider,
  useConversationContext,
} from '../Conversation/Context'
import { ComposerAddNoteButton } from '../Conversation/ComposerAddNoteButton'
import { ComposerModeToggle } from '../Conversation/ComposerModeToggle'
import { ConversationDetailsPanel } from '../Conversation/ConversationDetailsPanel'
import { ConversationComposerToolbarEnd } from '../Conversation/ConversationComposerToolbarEnd'
import { ConversationLayout } from '../Conversation/ConversationLayout'
import { LastBreadcrumbItem } from '../Conversation/LastBreadcrumbItem'
import { LeftContent } from '../Conversation/LeftContent'
import { useComposerAddNote } from '../Conversation/useComposerAddNote'
import { useComposerModeTint } from '../Conversation/useComposerModeTint'
import { useConversationInboxContext, InboxComposerProvider, useInboxComposerContext } from './Context'
import { InboxThreadLayout } from './InboxThreadLayout'
import {
  COMPOSER_DEFAULT_HEIGHT,
  COMPOSER_HEIGHT_STORAGE_KEY,
  COMPOSER_HIDDEN_STORAGE_KEY,
  COMPOSER_KEYBOARD_STEP,
  INBOX_COMPOSER_ID,
  composerStatusBarInit,
  composerToolbarInit,
} from './constants'
import { clampComposerHeight, getComposerMaxHeight } from './composerHeight.utils'
import { MergeConversation } from './MergeConversation'
import { MergedConversationNotice } from './MergedConversationNotice'
import { conversationDetailsQuery, markChatAsSpamMutation, unmarkChatAsSpamMutation } from './graphql'
import styles from './ConversationDetail.module.scss'

// The detail read also selects the merge target's code (`mergedIntoCode`), a read-only field the Chat contract
// class does not carry (it is declared on the server's ChatResolver).
type ChatDetail = Chat & { mergedIntoCode?: string | null }

enum ConversationTabEnum {
  Thread = 'Thread',
  History = 'History',
}

//unused by the inbox (the order-specific ticket editor is replaced by the merge action), but the
//provider requires a MutationConfig — build a real one from the chat fragment to satisfy the type
const chatMutationConfig = buildGraphqlQuery('Chat', chatFragment)

// Mode switch plus the unchanged rich-text editor. The staged message is the one `message` state
// ChannelProvider already keeps (Channel/Context.tsx), so switching modes leaves a half-written
// draft in place instead of throwing it away.
const InboxComposerInput = () => {
  const { threadHeight, titleHeight } = useInboxComposerContext()
  const { chatInputRef } = useChannelContext()
  const tintEditor = useComposerModeTint()
  const [storedHeight, setStoredHeight] = useLocalStorage<number>(COMPOSER_HEIGHT_STORAGE_KEY, COMPOSER_DEFAULT_HEIGHT)
  const [hidden, setHidden] = useLocalStorage<boolean>(COMPOSER_HIDDEN_STORAGE_KEY, false)
  const { height: windowHeight } = useWindowSize()
  const maxHeight = getComposerMaxHeight(windowHeight, threadHeight, titleHeight)
  const clampHeight = (value: number) => clampComposerHeight(value, maxHeight)
  // Rendering clamps; only a drag or a key writes storage.
  const composerHeight = clampHeight(toNumber(storedHeight) || COMPOSER_DEFAULT_HEIGHT)
  const composerHeightRef = useLatest(composerHeight)
  const dragStartHeightRef = React.useRef(composerHeight)
  const focusOnRestoreRef = React.useRef(false)

  const { onMouseDown } = useDragResize({
    cursor: 'ns-resize',
    onStart: () => {
      dragStartHeightRef.current = composerHeight
    },
    onDrag: ({ startY, clientY }) => setStoredHeight(clampHeight(dragStartHeightRef.current + startY - clientY)),
  })

  // Every other key, Home and End included, falls through untouched.
  const onSplitterKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      setHidden(true)
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setStoredHeight(clampHeight(composerHeight + COMPOSER_KEYBOARD_STEP))
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setStoredHeight(clampHeight(composerHeight - COMPOSER_KEYBOARD_STEP))
    }
  }

  const startReply = () => {
    focusOnRestoreRef.current = true
    setHidden(false)
  }

  // The `init` prop is read once — a changed height after mount is written straight onto the
  // container, the same inline height the editor's own grip writes.
  useEffect(() => {
    if (chatInputRef.current?.initialized && !chatInputRef.current.removed) {
      chatInputRef.current.getContainer().style.height = `${composerHeight}px`
    }
  }, [composerHeight, chatInputRef])

  useEffect(() => {
    if (!hidden && focusOnRestoreRef.current) {
      focusOnRestoreRef.current = false
      chatInputRef.current?.focus()
    }
  }, [hidden, chatInputRef])

  // COUPLING: the expanded root's `border-top` (1px) and `pt-3` (16px) are counted in COMPOSER_THREAD_CHROME_HEIGHT
  // in libs/chat/webui/src/Inbox/constants.ts.
  return (
    <div className={hidden ? undefined : 'position-relative border-top pt-3'}>
      <ShowIf condition={!hidden}>
        <HorizontalStack
          gap={1}
          className="position-absolute top-0 start-50 translate-middle bg-body border rounded-pill px-2"
        >
          <TooltipPopOver tooltipBody="Drag to resize">
            <div
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize composer"
              aria-controls={INBOX_COMPOSER_ID}
              aria-valuenow={composerHeight}
              aria-valuemin={0}
              aria-valuemax={maxHeight}
              tabIndex={0}
              onMouseDown={onMouseDown}
              onKeyDown={onSplitterKeyDown}
              className={`py-2 ${styles.composerSplitter}`}
            >
              <div className={`rounded-pill ${styles.composerSplitterPill}`} />
            </div>
          </TooltipPopOver>
          <BusyButton
            variant="link"
            bsSize="sm"
            aria-label="Hide composer"
            title="Hide composer"
            icon={
              <span aria-hidden="true">
                <Icon name="chevron-down" />
              </span>
            }
            onClick={() => setHidden(true)}
          />
        </HorizontalStack>
      </ShowIf>
      <ShowIf condition={!!hidden}>
        <BusyButton
          variant="secondary"
          className="w-100 py-2 lh-base fw-bold"
          text="Start reply"
          icon="chevron-up"
          onClick={startReply}
        />
      </ShowIf>
      <div id={INBOX_COMPOSER_ID} className={hidden ? `overflow-hidden invisible ${styles.composerHidden}` : undefined}>
        <RichTextChatInput
          height={composerHeight}
          init={{
            menubar: false,
            ...composerStatusBarInit,
            ...composerToolbarInit,
            setup: (editor) =>
              editor.on('init', () => {
                editor.getContainer().style.height = `${composerHeightRef.current}px`
                tintEditor(editor)
              }),
          }}
          toolbarStart={<ComposerModeToggle />}
          toolbarEnd={<ConversationComposerToolbarEnd />}
        />
      </div>
    </div>
  )
}

// Mode-aware send control. A public reply takes the channel's send path untouched; a note bypasses
// it entirely — that path mails first and persists after with no branch that suppresses the send,
// so routing a note through it would mail the customer. The thread is refetched so the note
// renders.
const InboxComposerSendButton = () => {
  const { refetchChat } = useInboxComposerContext()
  const { composerMode } = useConversationContext()
  const { message, status, onSend } = useChannelContext()
  const { onAddNote, savingNote } = useComposerAddNote()

  if (composerMode === ComposerModeEnum.Public) {
    const onSendReply = async () => {
      try {
        await onSend({ isPublic: true, format: 'html' })
      } finally {
        // Best-effort: a rejected send can still have moved the conversation server-side, and a
        // failed re-read must never mask the send's own error path.
        await refetchChat().catch(() => undefined)
      }
    }

    return (
      <BusyButton
        onClick={onSendReply}
        title="Send the message."
        busyText="Sending..."
        isBusy={status?.sending}
        disabled={status?.busy || isBlankHtml(message)}
        icon="paper-plane"
        text="Send"
        variant="primary"
        className={styles.composerSendButton}
      />
    )
  }

  return <ComposerAddNoteButton onClick={onAddNote} isBusy={savingNote} className={styles.composerSendButton} />
}

type RefreshThreadButtonProps = {
  // The header badge reads the page's detail document while the thread read below carries no
  // status, so a refresh re-reads both or the badge keeps showing the pre-refresh move.
  refetchChat: () => Promise<unknown>
}

const RefreshThreadButton = ({ refetchChat }: RefreshThreadButtonProps) => {
  const { _id: conversationId } = useParams()
  const { onRefresh } = useChatMessage({ conversationId: conversationId ?? '' })
  const [refreshing, setRefreshing] = useState(false)

  const onClick = async () => {
    setRefreshing(true)
    try {
      await onRefresh()
      await refetchChat()
    } finally {
      setRefreshing(false)
    }
  }

  return <RefreshButton loading={refreshing} onClick={onClick} title="Refresh the thread." />
}

type MarkAsSpamButtonProps = {
  chat?: Chat
}

// The band's spam control. Marking moves the conversation to Cancelled and records its sender on
// the tenant's spam-sender list; un-marking removes the sender and returns the conversation to
// New. It reads as Unmark only while the conversation is cancelled for an agent's marking — any
// other Cancelled conversation keeps the breadcrumb's status menu as its way back.
const MarkAsSpamButton = ({ chat }: MarkAsSpamButtonProps) => {
  const { onRefresh } = useConversationInboxContext()
  const { showSuccess } = useToast()
  const [markAsSpam, , { error: markError, loading: marking }] = useMutation<Chat, { chatId: string }>({
    query: markChatAsSpamMutation,
  })
  const [unmarkAsSpam, , { error: unmarkError, loading: unmarking }] = useMutation<Chat, { chatId: string }>({
    query: unmarkChatAsSpamMutation,
  })
  useError(markError)
  useError(unmarkError)

  if (!chat?._id) return null

  const isSpamMarked =
    chat.status === ChatStatusEnum.Cancelled && chat.cancelReason === ChatCancelReasonEnum.AgentSpamMarking

  const onClick = async () => {
    if (isSpamMarked) {
      await unmarkAsSpam({ input: { chatId: chat._id } })
      showSuccess({ message: 'Conversation un-marked as spam.' })
    } else {
      await markAsSpam({ input: { chatId: chat._id } })
      showSuccess({ message: 'Conversation marked as spam.' })
    }
    await onRefresh()
  }

  return (
    <BusyButton
      icon="ban"
      text={isSpamMarked ? 'Unmark as spam' : 'Mark as spam'}
      title={
        isSpamMarked
          ? 'Remove the sender from the spam-sender list and return the conversation to New.'
          : 'Move the conversation to Cancelled and record the sender on the spam-sender list.'
      }
      onClick={onClick}
      isBusy={marking || unmarking}
    />
  )
}

export type ConversationDetailProps = {
  /** Puts an icon before each tab's text. Off by default, so a host that does not opt in renders as before. */
  showTabIcons?: boolean
}

export const ConversationDetail = ({ showTabIcons }: ConversationDetailProps) => {
  const { _id: conversationId } = useParams()
  const {
    overviewRoute,
    conversationRoute,
    rootUrl,
    storeUrl,
    communicationTemplateCategories,
    templateSelectComponent,
  } = useConversationHost()
  const { renderDocumentCell } = useConversationInboxContext()
  const { hasRole: canEdit } = useRole({ roleName: chatPermissions.canEdit.value })
  const CreateOrderFromConversationButton = useInjectComponent<CreateOrderFromConversationButtonProps>(
    WebuiIocKey.order.createOrderFromConversationButton
  )
  const TemplateVariablesProvider = useInjectComponent<
    ConversationTemplateVariablesProviderProps<TemplateVariables, Record<any, Partial<ConfirmDialogOptions>>>
  >(WebuiIocKey.chat.conversationTemplateVariablesProvider)
  const isMobile = useIsMobileScreenSize()
  // The same detail read the rail uses (ConversationDetailsPanel.tsx) — the inbox list page's
  // `chatList` only carries whatever page happens to be loaded, so a conversation opened off that
  // page (e.g. via search or a direct link) left the header empty.
  const {
    document: chat,
    error,
    isEmpty,
    refetch: refetchChat,
  } = useDocument<ChatDetail>({
    query: conversationDetailsQuery,
    filter: { externalId: conversationId },
    subscribeToUpdates: true,
  })
  // A conversation that was merged into another stays merged whatever its status moves to.
  const isMerged = !!chat?.mergedInto
  // Opening a conversation reads it — the mount sits behind the host's `chat.canView` page gate —
  // and so does a customer reply that lands while it stays open (the count rises again). The
  // mutation returns the zeroed count, which the cache writes back to the inbox row. Best-effort:
  // a failed mark must not break the thread, and the call rides a ref so the effect re-runs only
  // when the conversation or its count changes, never on a fresh function identity.
  const [markChatRead] = useMarkChatRead()
  const markChatReadRef = useLatest(markChatRead)
  const unreadCount = chat?.unreadForAgentCount ?? 0
  useEffect(() => {
    if (!chat?._id || unreadCount <= 0) return
    markChatReadRef.current({ chatId: chat._id }).catch(() => undefined)
  }, [chat?._id, unreadCount, markChatReadRef])

  //generic send path — routes through the channel selector; the server guard enforces access
  const sendChatMessageResult = useSendChatMessage()
  // Held here, not in ConversationProvider: opening another conversation swaps the template-variables
  // provider above it (order chat vs orphan chat), which remounts ConversationProvider and would drop
  // its own state. This page stays mounted across the switch.
  const [composerMode, setComposerMode] = useState(ComposerModeEnum.Public)
  const [threadRef, { height: threadHeight }] = useMeasure<HTMLDivElement>()

  // Memoized on `canEdit` alone — a fresh object (or a fresh arrow inside it) each render is a new
  // component type once the provider merges it, and React remounts the composer and the template
  // select mid-action.
  const components = useMemo(
    () => ({
      // Without the edit permission the provider's own swap hides the composer and the action
      // band; the inbox overrides state the same swap explicitly so no send path stays mounted.
      input: canEdit && !isMerged ? InboxComposerInput : NullComponent,
      sendMessageButton: canEdit && !isMerged ? InboxComposerSendButton : NullComponent,
      chatActions: {
        // Single merge entry: the header band holds the only merge slot, so the composer-left
        // duplicate is dropped while the rail's Continued-from rows stay the merge-history
        // surface.
        leftContent: canEdit && !isMerged ? LeftContent : NullComponent,
      },
      // The inbox's own scroll region + bare sticky composer, in place of `DefaultLayout`
      // (Chat/chatComponents.tsx).
      layout: InboxThreadLayout,
    }),
    [canEdit, isMerged]
  )

  if (!conversationId) {
    return <EmptyContent message="Select a conversation to read and reply." />
  }

  // A rejected read (e.g. a missing `chat.canView`) shows the server's message, never an empty thread.
  if (error) {
    return <ErrorContent error={error} action={<GoBackButton />} />
  }

  if (isEmpty) {
    return (
      <EmptyContent
        icon="inbox"
        action={<GoBackButton />}
        message="Conversation detail cannot be shown."
        subMessage="Conversation does not exist or you do not have permission to view it."
      />
    )
  }

  const subjectLabel = truncate(chat?.subject ?? '', 50)
  const title = chat?.code ? `${chat.code} — ${subjectLabel}` : subjectLabel
  const items: BreadcrumbItem[] = [
    overviewRoute,
    conversationRoute,
    {
      component: <LastBreadcrumbItem code={chat?.code} chat={chat} />,
    },
  ]
  // Sales-shaped trail (apps/panelpunches/admin/src/view/sales/item/index.tsx): a phone-width
  // screen shows the last crumb alone instead of the full trail.
  const breadcrumbs = isMobile ? <LastBreadcrumbItem code={chat?.code} chat={chat} /> : <Breadcrumb items={items} />

  return (
    <TemplateVariablesProvider chat={chat}>
      {({ templateVariables, templateVariableConfirmDialogConfigs }) => (
        <ConversationProvider
          conversationId={conversationId}
          hasRole={canEdit}
          recipient={chat?.sender?.profile}
          documentId={chat?._id ?? conversationId ?? ''}
          members={[]}
          subject={chat?.subject ?? ''}
          templateCategory={communicationTemplateCategories}
          templateVariables={templateVariables}
          templateVariableConfirmDialogConfigs={templateVariableConfirmDialogConfigs}
          templateSelectComponent={templateSelectComponent}
          components={components}
          composerMode={composerMode}
          setComposerMode={setComposerMode}
          sendChatMessageResult={sendChatMessageResult}
          updateTicketNoMutationConfig={chatMutationConfig.updateConfig}
          useFor={ConversationForEnum.SalesOrder}
          rootUrl={rootUrl}
          storeUrl={storeUrl}
        >
          <FixedPage.Header
            title={title}
            breadcrumbs={breadcrumbs}
            actions={
              <ButtonGroup size={isMobile ? 'sm' : undefined}>
                <RefreshThreadButton refetchChat={refetchChat} />
                <CreateOrderFromConversationButton chat={chat} />
                <ShowIf condition={canEdit && !isMerged}>
                  <MergeConversation sourceChat={chat} />
                </ShowIf>
                <ShowIf condition={canEdit}>
                  <MarkAsSpamButton chat={chat} />
                </ShowIf>
              </ButtonGroup>
            }
          />
          <FixedPage.Body>
            <CollapsibleSidebar
              sidebar={<ConversationDetailsPanel renderDocumentCell={renderDocumentCell} />}
              storageKey="conversation-collapsible-sidebar"
            >
              <div className={`${styles.detailTabs} d-flex flex-column flex-fill`}>
                <TabWithSearchParams
                  sticky
                  tabName="conversation-tabs"
                  id="conversation-detail-tab"
                  defaultActiveKey={ConversationTabEnum.Thread}
                  variant="underline"
                  className="ps-3"
                  mountOnEnter
                >
                  <Tab
                    eventKey={ConversationTabEnum.Thread}
                    title={
                      <TabTitle title={ConversationTabEnum.Thread} icon={showTabIcons ? 'comment-dots' : undefined} />
                    }
                    className="flex-column h-100 pt-3"
                  >
                    <InboxComposerProvider threadHeight={threadHeight} refetchChat={refetchChat}>
                      {/* The inbox thread fills the pane beside the rail: the message region scrolls inside it
                          while the composer and the details rail stay fixed. */}
                      <div ref={threadRef} className={`${styles.detailThread} d-flex flex-column flex-fill`}>
                        <ConversationLayout />
                      </div>
                      <ShowIf condition={isMerged}>
                        <MergedConversationNotice targetId={chat?.mergedInto} targetCode={chat?.mergedIntoCode} />
                      </ShowIf>
                    </InboxComposerProvider>
                  </Tab>
                  <Tab
                    eventKey={ConversationTabEnum.History}
                    title={<TabTitle title={ConversationTabEnum.History} icon={showTabIcons ? 'history' : undefined} />}
                    className="flex-column h-100 pt-2"
                  >
                    <HistoryLayout
                      filter={{ $and: [{ entity: 'Chat' }, { entityKey: { $in: [chat?._id ?? ''] } }] }}
                      filterCategory="filter_chat_history"
                      height={{ topMargin: 280 }}
                    />
                  </Tab>
                </TabWithSearchParams>
              </div>
            </CollapsibleSidebar>
          </FixedPage.Body>
        </ConversationProvider>
      )}
    </TemplateVariablesProvider>
  )
}
